/**
 * Help.
 *
 * WHAT THIS IS FOR. Almost every screen in this product answers "is this
 * machine backed up", and almost none of them explain WHY it decided that. The
 * status pill says Warning; the reason line says "expected every 24 hours"; and
 * the question a new technician actually has - who decided 24, and can I change
 * it - has until now had no answer anywhere in the interface.
 *
 * So this page is not a feature tour. It is the small number of rules that
 * govern every judgement on the board, written down once, in the order somebody
 * hits them.
 *
 * Deliberately plain text with no live data. A help page that queries anything
 * is a help page that can be down at the moment somebody needs it.
 */

export function Help() {
  return (
    <div className="settings">
      <section className="panel">
        <h2 className="panel-title">How a status is decided</h2>
        <p className="panel-note" style={{ marginTop: 0 }}>
          Every machine gets the <strong>worse</strong> of two judgements. That is the whole model,
          and it is deliberate: a vendor reporting "OK" can never hide a backup that has not run.
        </p>
        <dl className="kv">
          <dt>The vendor's own flag</dt>
          <dd>
            What the backup software says about the last run - paused, archived, errored, never
            produced a snapshot. No schedule or policy may overturn these; they are facts, not
            expectations.
          </dd>
          <dt>How long since it worked</dt>
          <dd>
            Measured against what this machine is <em>expected</em> to do. This is the half a
            policy can change.
          </dd>
        </dl>
      </section>

      <section className="panel">
        <h2 className="panel-title">Where the expectation comes from</h2>
        <p className="panel-note" style={{ marginTop: 0 }}>
          Six places, most specific first. The first one that says anything wins, and the machine's
          reason line always names which.
        </p>
        <table className="mini-table">
          <tbody>
            <tr><td style={{ width: 150 }}><strong>1. This machine</strong></td>
              <td className="dim">
                Open any row on the board and set a policy on it. Beats everything else.
              </td></tr>
            <tr><td><strong>2. This client</strong></td>
              <td className="dim">Set under Configuration &rarr; Policies. Applies to all their machines.</td></tr>
            <tr><td><strong>3. The software</strong></td>
              <td className="dim">
                StorageCraft and ShadowControl publish each job's own run days, window and repeat.
                Those machines need no configuration at all.
              </td></tr>
            <tr><td><strong>4. Observed</strong></td>
              <td className="dim">
                Datto, MSP360 and Kaseya publish no schedule, so we measure how long the machine
                normally goes quiet. This can only ever <em>extend</em> patience - an inference is
                never allowed to declare an outage sooner than the standard rule.
              </td></tr>
            <tr><td><strong>5. Your default</strong></td>
              <td className="dim">Configuration &rarr; Policies, top of the page.</td></tr>
            <tr><td><strong>6. Built-in</strong></td>
              <td className="dim">Nightly: a warning after 30 hours, a failure after 54.</td></tr>
          </tbody>
        </table>
      </section>

      <section className="panel">
        <h2 className="panel-title">What each page is for</h2>
        <dl className="kv">
          <dt>Overview</dt>
          <dd>Every machine, grouped by client. The one screen to look at each morning.</dd>
          <dt>Status changes</dt>
          <dd>
            Transitions only, never a reading every fifteen minutes. If a machine is not in here it
            has not changed.
          </dd>
          <dt>Hidden Endpoints</dt>
          <dd>
            Machines excluded from every count. Kept visible on purpose - a suppression nobody can
            see is one nobody revisits.
          </dd>
          <dt>Review</dt>
          <dd>
            Things awaiting a human decision: new clients from a vendor, schedules that differ from
            their peers, policies that have stopped describing reality.
          </dd>
          <dt>Knowledge</dt>
          <dd>
            Notes attached to a machine or a client. Anyone can write here, deliberately - the
            people working the board learn these things first.
          </dd>
          <dt>Policies</dt>
          <dd>What a backup is expected to do, and therefore what counts as late.</dd>
          <dt>Alerts</dt>
          <dd>
            Who gets emailed and when. Alerts fire on the <em>change</em>, so a machine that stays
            broken over a weekend produces one email, not ninety-six.
          </dd>
          <dt>Integrations</dt>
          <dd>Vendor credentials, PSA ticketing, documentation and compliance.</dd>
        </dl>
      </section>

      <section className="panel">
        <h2 className="panel-title">If something looks wrong</h2>
        <p className="panel-note" style={{ marginTop: 0 }}>
          Two buttons at the bottom of the sidebar, and they go to different places on purpose.
        </p>
        <dl className="kv">
          <dt>The bug icon</dt>
          <dd>
            Something is broken or reporting the wrong thing. It captures the page you were on, so
            report it from where you saw it rather than describing it afterwards.
          </dd>
          <dt>The bulb icon</dt>
          <dd>Something is missing. Feature requests go to the same board we plan from.</dd>
        </dl>
      </section>
    </div>
  );
}
